/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import type { MatrixClient } from "matrix-js-sdk";

export const backendUrl = import.meta.env.VITE_BACKEND_URL || "";
export class BackendError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "BackendError";
  }
}

// Never send a Matrix access token to a focus discovered from another server.
export function sfuAuthHeaders(
  serviceUrl: string,
  token?: string,
): Record<string, string> {
  if (!token) return {};
  const trusted = [
    backendUrl || window.location.origin,
    import.meta.env.VITE_ADMIN_URL,
    import.meta.env.VITE_GUEST_URL,
  ]
    .filter(Boolean)
    .map((url) => new URL(url, window.location.origin).origin);
  const service = new URL(serviceUrl, window.location.origin);
  return !service.username &&
    !service.password &&
    trusted.includes(service.origin)
    ? { Authorization: `Bearer ${token}` }
    : {};
}

export async function backendFetch(
  client: MatrixClient | undefined,
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(options.headers);
  const token = client?.getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(`${backendUrl}${endpoint}`, {
    ...options,
    headers,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new BackendError(
      body.error || `Request failed (${response.status})`,
      response.status,
    );
  }
  return response;
}

export async function recordingShare(
  client: MatrixClient | undefined,
  id: string,
): Promise<string> {
  const response = await backendFetch(
    client,
    `/api/recordings/${encodeURIComponent(id)}/share`,
    { method: "POST" },
  );
  return (await response.json()).url;
}
